import { useThemeContext } from "@/core/composables";
import {
  LOYALTY_CURRENCY_KEY,
  LOYALTY_ENABLED_KEY,
  LOYALTY_MODE_KEY,
  LOYALTY_MODULE_ID,
} from "@/core/constants/modules";
import { VirtoPermissions, CustomPermissions } from "@/core/enums";
import { useUser } from "@/shared/account";
import { ROUTES } from "@/router/routes/constants";
import { accountRoutes, getFirstVisibleAccountRouteName } from "./account";
import { cartRoutes } from "./cart";
// import { checkoutRoutes } from "./checkout";
import { corporateRoutes } from "./company";
import { customCheckoutRoutes } from "./custom-checkout";
import type { RouteRecordRaw } from "vue-router";
import Error400 from "@/pages/400.vue";
import Error403 from "@/pages/403.vue";
import Error404 from "@/pages/404.vue";
import Error500 from "@/pages/500.vue";

const callback = () => import("@/pages/auth/callback.vue");
const SingInPage = () => import("@/pages/sign-in.vue");
const SignUpPage = () => import("@/pages/innovadis/inno-sign-up.vue");
const ConfirmEmail = () => import("@/pages/confirm-email.vue");
const ConfirmInvitation = () => import("@/pages/confirm-invitation.vue");
const ForgotPassword = () => import("@/pages/forgot-password.vue");
const ResetPassword = () => import("@/pages/reset-password.vue");
const ChangePassword = () => import("@/pages/change-password.vue");
const BlockedPage = () => import("@/pages/blocked.vue");
const Account = () => import("@/pages/account/index.vue");
const Impersonate = () => import("@/pages/account/impersonate.vue");
const Company = () => import("@/pages/company/index.vue");
const BulkOrder = () => import("@/pages/bulk-order.vue");
const CompareProducts = () => import("@/pages/innovadis/inno-compare-products.vue");
const Search = () => import("@/pages/search.vue");
const Catalog = () => import("@/pages/catalog.vue");
const LoyaltyCatalog = () => import("@/pages/loyalty-catalog.vue");
const Category = () => import("@/pages/category.vue");
const ProductRoute = () => import("@/pages/innovadis/inno-product.vue");
const SharedList = () => import("@/pages/shared-list.vue");
const Branch = () => import("@/pages/branch.vue");
const Welcome = () => import("@/pages/welcome.vue");
const Matcher = () => import("@/pages/matcher/matcher.vue");
const ImpersonateUserDashboard = () => import("@/pages/impersonate-user.vue");

const LOYALTY_CATALOG_MODES = new Set(["Mixed Cart", "Loyalty Store"]);

function isLoyaltyCatalogAvailable(): boolean {
  const { themeContext } = useThemeContext();
  const loyaltyModule = themeContext.value?.storeSettings?.modules?.find(
    (module) => module.moduleId === LOYALTY_MODULE_ID,
  );
  const settings = loyaltyModule?.settings ?? [];
  const isEnabled = settings.find((s) => s.name === LOYALTY_ENABLED_KEY)?.value === true;
  const currency = settings.find((s) => s.name === LOYALTY_CURRENCY_KEY)?.value as string | undefined;
  const mode = settings.find((s) => s.name === LOYALTY_MODE_KEY)?.value as string | undefined;
  return isEnabled && !!currency && !!mode && LOYALTY_CATALOG_MODES.has(mode);
}

export const mainRoutes: RouteRecordRaw[] = [
  { path: "/auth/callback", name: "AuthCallback", component: callback, meta: { public: true, redirectable: false } },
  { path: "/400", name: "BadRequest", component: Error400, meta: { public: true, redirectable: false } },
  { path: "/403", name: "NoAccess", component: Error403, meta: { public: true, redirectable: false } },
  { path: "/404", name: "NotFound", component: Error404, meta: { public: true, redirectable: false } },
  { path: "/500", name: "InternalError", component: Error500, meta: { public: false, redirectable: false } },
  {
    path: "/choose-login-administrator",
    name: "SignInAdministrator",
    component: ImpersonateUserDashboard,
    beforeEnter(to, from, next) {
      const { isAuthenticated, checkPermissions } = useUser();
      const canImpersonate = checkPermissions(VirtoPermissions.LoginOnBehalf);

      if (!isAuthenticated.value || !canImpersonate) {
        next("/sign-in?returnUrl=/choose-login-administrator");
      } else {
        next();
      }
    },
    meta: { requiresAuth: true, requiredAdmin: true },
  },
  { path: ROUTES.SIGN_IN.PATH, name: ROUTES.SIGN_IN.NAME, component: SingInPage, meta: { public: true } },
  { path: "/sign-up", name: "SignUp", component: SignUpPage, meta: { public: true } },
  { path: "/confirm-invitation", name: "ConfirmInvitation", component: ConfirmInvitation, meta: { public: true } },
  { path: "/forgot-password", name: "ForgotPassword", component: ForgotPassword, meta: { public: true } },
  { path: "/reset-password", name: "ResetPassword", component: ResetPassword, meta: { public: true } },
  {
    path: ROUTES.CHANGE_PASSWORD.PATH,
    name: ROUTES.CHANGE_PASSWORD.NAME,
    component: ChangePassword,
    meta: { public: false, redirectable: false },
  },
  { path: "/set-password", name: "SetPassword", component: ResetPassword, meta: { public: true } },
  { path: "/blocked", name: "Blocked", component: BlockedPage, meta: { public: true, redirectable: false } },
  { path: "/account/confirmemail", name: "ConfirmEmail", component: ConfirmEmail, meta: { public: true } },
  {
    path: "/account/impersonate/:userId",
    name: "Impersonate",
    props: true,
    component: Impersonate,
    meta: { public: true },
  },
  {
    path: "/account",
    name: "Account",
    component: Account,
    children: accountRoutes,
    // Innovadis custom: send users to the first visible account route, including Dashboard when available
    redirect: () => ({ name: getFirstVisibleAccountRouteName() }),
    meta: { requiresAuth: true },
  },
  {
    path: "/company",
    name: "Company",
    component: Company,
    children: corporateRoutes,
    redirect: { name: corporateRoutes[0].name },
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
    },
  },
  { path: "/branch/:branchId", name: "BranchPage", component: Branch, props: true },
  { path: ROUTES.SEARCH.PATH, name: ROUTES.SEARCH.NAME, component: Search },
  {
    path: "/bulk-order",
    name: "BulkOrder",
    component: BulkOrder,
    // Innovadis custom: shield the bulk order route behind a permission check
    beforeEnter(_to, _from, next) {
      const { isAuthenticated, checkPermissions } = useUser();
      const { ShowBulkOrder } = CustomPermissions;
      if (!isAuthenticated.value || !checkPermissions(ShowBulkOrder)) {
        next({ name: ROUTES.CATALOG.NAME });
      } else {
        next();
      }
    },
  },
  { path: "/compare", name: "CompareProducts", component: CompareProducts },
  { path: "/successful-registration", name: "Welcome", component: Welcome, meta: { public: true } },
  ...cartRoutes,
  ...customCheckoutRoutes,
  { path: ROUTES.CATALOG.PATH, name: ROUTES.CATALOG.NAME, component: Catalog, props: true },
  {
    path: ROUTES.LOYALTY_CATALOG.PATH,
    name: ROUTES.LOYALTY_CATALOG.NAME,
    component: LoyaltyCatalog,
    beforeEnter: (_to, _from, next) => {
      return isLoyaltyCatalogAvailable() ? next() : next({ name: "NotFound" });
    },
  },
  {
    path: ROUTES.LOYALTY_PRODUCT.PATH,
    name: ROUTES.LOYALTY_PRODUCT.NAME,
    component: ProductRoute,
    props: (route) => ({ allowSetMeta: true, productId: route.params.productId }),
    beforeEnter: (_to, _from, next) => {
      return isLoyaltyCatalogAvailable() ? next() : next({ name: "NotFound" });
    },
  },
  {
    path: ROUTES.LOYALTY_CATEGORY.PATH,
    name: ROUTES.LOYALTY_CATEGORY.NAME,
    component: Category,
    props: true,
    beforeEnter: (_to, _from, next) => {
      return isLoyaltyCatalogAvailable() ? next() : next({ name: "NotFound" });
    },
  },
  {
    path: `${ROUTES.LOYALTY_CATALOG.PATH}/:pathMatch(.*)*`,
    name: "LoyaltyCatalogMatcher",
    component: Matcher,
    props: true,
    beforeEnter: (_to, _from, next) => {
      return isLoyaltyCatalogAvailable() ? next() : next({ name: "NotFound" });
    },
  },
  {
    path: ROUTES.PROMOTIONS.PATH,
    name: ROUTES.PROMOTIONS.NAME,
    component: Category,
    props: () => ({ filter: '"tws_type":"promotion"', title: "Acties" }),
  },
  {
    path: ROUTES.NEW.PATH,
    name: ROUTES.NEW.NAME,
    component: Category,
    props: () => ({ filter: '"tws_type":"new"', title: "Nieuw" }),
  },
  { path: "/category/:categoryId", name: "Category", component: Category, props: true },
  {
    path: "/product/:productId",
    name: "Product",
    component: ProductRoute,
    props: (route) => ({ allowSetMeta: true, productId: route.params.productId }),
  },
  { path: "/shared-list/:sharingKey", name: "SharedList", component: SharedList, props: true },

  /** NOTE: Always leave it last. */
  { path: "/:pathMatch(.*)*", name: "Matcher", component: Matcher, props: true },
];
